import React, { JSX, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../context/ThemeContext";

type DropdownOption = {
  label: string;
  checked?: boolean;
  render?: JSX.Element;
};

type ActionButtonProps = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "neutral";
  textColor?: string;
  dropdownOptions?: DropdownOption[];
  onOptionChange?: (index: number, checked: boolean) => void;
  disabled?: boolean;
};

type TableActionsProps = {
  buttons?: ActionButtonProps[];
  buttonMarginRight?: string | number;
  buttonGap?: number | string;
  outerMarginLeft?: string | number;
  outerMarginRight?: string | number;
  fullBleedLine?: boolean;
  marginTop?: number | string;
};

type DropdownPosition = {
  top: number;
  left: number;
  width: number;
};

const LAYOUT_SIDE_PADDING = 20;
const SYSTEM_BASE_COLOR = "#0e333a";
const DROPDOWN_WIDTH = 286;
const DROPDOWN_Z_INDEX = 999999;

const clamp = (value: number, min = 0, max = 255) =>
  Math.max(min, Math.min(max, value));

const normalizeHex = (hex?: string) => {
  const raw = (hex || SYSTEM_BASE_COLOR).replace("#", "").trim();

  if (/^[0-9A-Fa-f]{3}$/.test(raw)) {
    return raw
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if (/^[0-9A-Fa-f]{6}$/.test(raw)) {
    return raw;
  }

  return SYSTEM_BASE_COLOR.replace("#", "");
};

const adjustHex = (hex: string, amount: number) => {
  const full = normalizeHex(hex);

  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);

  const nr = clamp(r + amount);
  const ng = clamp(g + amount);
  const nb = clamp(b + amount);

  return `#${nr.toString(16).padStart(2, "0")}${ng
    .toString(16)
    .padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
};

const hexToRgba = (hex: string, alpha: number) => {
  const full = normalizeHex(hex);

  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const parseSize = (value?: string | number): string => {
  if (value === undefined || value === null) return "0px";
  return typeof value === "number" ? `${value}px` : value;
};

const TableActions: React.FC<TableActionsProps> = ({
  buttons = [],
  buttonMarginRight,
  buttonGap = 8,
  outerMarginLeft,
  outerMarginRight,
  fullBleedLine = false,
  marginTop = 0,
}) => {
  const { departmentColor } = useTheme();

  const departmentBase = departmentColor || SYSTEM_BASE_COLOR;

  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [hoveredButton, setHoveredButton] = useState<number | null>(null);
  const [pressedButton, setPressedButton] = useState<number | null>(null);

  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const [ui, setUi] = useState({
    fontSize: 12,
    buttonHeight: 31,
    horizontalPadding: 14,
  });

  const palette = useMemo(() => {
    const dept = departmentBase;
    const system = SYSTEM_BASE_COLOR;

    return {
      department: dept,
      system,

      primary: dept,
      primaryHover: adjustHex(dept, 9),
      primaryActive: adjustHex(dept, -13),

      secondary: adjustHex(dept, 15),
      secondaryHover: adjustHex(dept, 24),
      secondaryActive: adjustHex(dept, -10),

      neutral: system,
      neutralHover: adjustHex(system, 9),
      neutralActive: adjustHex(system, -12),

      white: "#ffffff",
      surface: "#ffffff",

      buttonShadow: `0 3px 8px ${hexToRgba(system, 0.14)}`,
      buttonShadowHover: `0 5px 13px ${hexToRgba(system, 0.2)}`,
      buttonShadowPressed: `0 2px 5px ${hexToRgba(system, 0.16)}`,

      buttonInset: "inset 0 1px 0 rgba(255,255,255,0.10)",

      dropdownBorder: "rgba(14, 51, 58, 0.14)",
      dropdownText: system,
      dropdownMutedText: "rgba(15, 23, 42, 0.62)",
      dropdownLine: "rgba(226, 232, 240, 0.92)",
      dropdownBg: "#ffffff",
      dropdownHeaderBg:
        "linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(255,255,255,1) 100%)",
      dropdownItemHover: hexToRgba(dept, 0.06),
    };
  }, [departmentBase]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 1000) {
        setUi({
          fontSize: 11.5,
          buttonHeight: 29,
          horizontalPadding: 11,
        });
        return;
      }

      if (width < 1400) {
        setUi({
          fontSize: 12,
          buttonHeight: 31,
          horizontalPadding: 13,
        });
        return;
      }

      setUi({
        fontSize: 12.5,
        buttonHeight: 33,
        horizontalPadding: 15,
      });
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const updateDropdownPosition = (index: number) => {
    const button = buttonRefs.current[index];
    if (!button) return;

    const rect = button.getBoundingClientRect();

    const viewportPadding = 12;
    const availableRight = window.innerWidth - viewportPadding;

    let left = rect.right - DROPDOWN_WIDTH;

    if (left < viewportPadding) {
      left = viewportPadding;
    }

    if (left + DROPDOWN_WIDTH > availableRight) {
      left = availableRight - DROPDOWN_WIDTH;
    }

    setDropdownPosition({
      top: rect.bottom + 8,
      left,
      width: DROPDOWN_WIDTH,
    });
  };

  useEffect(() => {
    if (openDropdown === null) return;

    updateDropdownPosition(openDropdown);

    const handleScrollOrResize = () => {
      updateDropdownPosition(openDropdown);
    };

    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [openDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);

      const clickedInsideButton =
        openDropdown !== null &&
        buttonRefs.current[openDropdown]?.contains(target);

      if (!clickedInsideDropdown && !clickedInsideButton) {
        setOpenDropdown(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openDropdown]);

  const getButtonColors = (variant?: ActionButtonProps["variant"]) => {
    if (variant === "secondary") {
      return {
        base: palette.secondary,
        hover: palette.secondaryHover,
        active: palette.secondaryActive,
      };
    }

    if (variant === "neutral") {
      return {
        base: palette.neutral,
        hover: palette.neutralHover,
        active: palette.neutralActive,
      };
    }

    return {
      base: palette.primary,
      hover: palette.primaryHover,
      active: palette.primaryActive,
    };
  };

  const getButtonStyle = (
    btn: ActionButtonProps,
    index: number
  ): React.CSSProperties => {
    const isHovered = hoveredButton === index;
    const isPressed = pressedButton === index;
    const isOpen = openDropdown === index;
    const isDisabled = !!btn.disabled;

    const colors = getButtonColors(btn.variant);

    let background = colors.base;
    let shadow = `${palette.buttonInset}, ${palette.buttonShadow}`;
    let transform = "translateY(0)";
    let filter = "none";

    if ((isHovered || isOpen) && !isDisabled) {
      background = colors.hover;
      shadow = `${palette.buttonInset}, ${palette.buttonShadowHover}`;
      transform = "translateY(-1px)";
      filter = "brightness(1.015)";
    }

    if (isPressed && !isDisabled) {
      background = colors.active;
      shadow = `${palette.buttonInset}, ${palette.buttonShadowPressed}`;
      transform = "translateY(1px)";
      filter = "brightness(0.985)";
    }

    return {
      ...styles.button,
      height: ui.buttonHeight,
      minHeight: ui.buttonHeight,
      padding: `0 ${ui.horizontalPadding}px`,
      fontSize: ui.fontSize,
      color: btn.textColor || palette.white,
      backgroundColor: background,
      border: "none",
      boxShadow: shadow,
      transform,
      filter,
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.52 : 1,
    };
  };

  const handleButtonClick = (btn: ActionButtonProps, index: number) => {
    if (btn.disabled) return;

    if (btn.dropdownOptions?.length) {
      setOpenDropdown((current) => {
        const next = current === index ? null : index;

        if (next !== null) {
          window.requestAnimationFrame(() => updateDropdownPosition(index));
        } else {
          setDropdownPosition(null);
        }

        return next;
      });

      return;
    }

    btn.onClick();
  };

  const outerRight = parseSize(outerMarginRight);
  const outerLeft = parseSize(outerMarginLeft);
  const mt = parseSize(marginTop);

  const wrapperWidth = fullBleedLine
    ? `calc(100% + ${LAYOUT_SIDE_PADDING * 2}px)`
    : "100%";

  const wrapperLeft = fullBleedLine ? `-${LAYOUT_SIDE_PADDING}px` : "0px";

  if (!buttons.length) return null;

  return (
    <div
      style={{
        ...styles.wrapper,
        marginTop: mt,
        marginLeft: outerLeft,
        marginRight: outerRight,
        width: wrapperWidth,
        left: wrapperLeft,
      }}
    >
      <div style={styles.actionsRow}>
        <div style={{ ...styles.buttons, gap: buttonGap }}>
          {buttons.map((btn, index) => {
            const hasDropdown = !!btn.dropdownOptions?.length;
            const isOpen = openDropdown === index;

            return (
              <div
                key={`${btn.label}-${index}`}
                style={{
                  ...styles.buttonSlot,
                  marginRight: buttonMarginRight ?? 0,
                }}
              >
                <button
                  ref={(element) => {
                    buttonRefs.current[index] = element;
                  }}
                  type="button"
                  disabled={btn.disabled}
                  aria-expanded={hasDropdown ? isOpen : undefined}
                  onClick={() => handleButtonClick(btn, index)}
                  onMouseEnter={() => setHoveredButton(index)}
                  onMouseLeave={() => {
                    setHoveredButton(null);
                    setPressedButton(null);
                  }}
                  onMouseDown={() => setPressedButton(index)}
                  onMouseUp={() => setPressedButton(null)}
                  style={getButtonStyle(btn, index)}
                >
                  <span style={styles.buttonLabel}>{btn.label}</span>
                </button>

                {hasDropdown &&
                  isOpen &&
                  dropdownPosition &&
                  createPortal(
                    <div
                      ref={dropdownRef}
                      style={{
                        ...styles.dropdown,
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        border: `1px solid ${palette.dropdownBorder}`,
                        background: palette.dropdownBg,
                        boxShadow: `0 18px 42px ${hexToRgba(
                          SYSTEM_BASE_COLOR,
                          0.22
                        )}`,
                      }}
                    >
                      <div
                        style={{
                          ...styles.dropdownHeader,
                          background: palette.dropdownHeaderBg,
                          borderBottom: `1px solid ${palette.dropdownLine}`,
                        }}
                      >
                        <span style={styles.dropdownTitle}>
                          Opções de exportação
                        </span>

                        <span
                          style={{
                            ...styles.dropdownSubtitle,
                            color: palette.dropdownMutedText,
                          }}
                        >
                          Selecione os campos que deseja incluir
                        </span>
                      </div>

                      <div style={styles.optionsList}>
                        {btn.dropdownOptions?.map((opt, optIndex) => (
                          <label
                            key={`${opt.label}-${optIndex}`}
                            style={styles.option}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.backgroundColor =
                                palette.dropdownItemHover;
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!opt.checked}
                              onChange={(event) =>
                                btn.onOptionChange?.(
                                  optIndex,
                                  event.target.checked
                                )
                              }
                              style={{
                                ...styles.checkbox,
                                accentColor: palette.department,
                              }}
                            />

                            <span
                              style={{
                                ...styles.optionLabel,
                                color: palette.dropdownText,
                              }}
                            >
                              {opt.label}
                            </span>

                            {opt.render && (
                              <span style={styles.optionRender}>
                                {opt.render}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>

                      <div
                        style={{
                          ...styles.dropdownFooter,
                          borderTop: `1px solid ${palette.dropdownLine}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdown(null);
                            setDropdownPosition(null);
                            btn.onClick();
                          }}
                          style={{
                            ...styles.confirmButton,
                            height: ui.buttonHeight,
                            minHeight: ui.buttonHeight,
                            fontSize: ui.fontSize,
                            backgroundColor: palette.department,
                            color: palette.white,
                            boxShadow: `${palette.buttonInset}, ${palette.buttonShadow}`,
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.backgroundColor =
                              palette.primaryHover;
                            event.currentTarget.style.boxShadow = `${palette.buttonInset}, ${palette.buttonShadowHover}`;
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.backgroundColor =
                              palette.department;
                            event.currentTarget.style.boxShadow = `${palette.buttonInset}, ${palette.buttonShadow}`;
                          }}
                          onMouseDown={(event) => {
                            event.currentTarget.style.backgroundColor =
                              palette.primaryActive;
                            event.currentTarget.style.transform =
                              "translateY(1px)";
                          }}
                          onMouseUp={(event) => {
                            event.currentTarget.style.backgroundColor =
                              palette.primaryHover;
                            event.currentTarget.style.transform =
                              "translateY(0)";
                          }}
                        >
                          Gerar PDF
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    position: "relative",
    width: "100%",
    zIndex: 20,
  },

  actionsRow: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },

  buttons: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    paddingBottom: 4,
    paddingRight: 0,
    marginLeft: 24,
  },

  buttonSlot: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    fontFamily: "'Open Sans', Inter, system-ui, sans-serif",
    fontWeight: 600,
    lineHeight: 1,

    borderRadius: 0,
    marginBottom: 8,
    outline: "none",

    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    userSelect: "none",

    transition:
      "transform 140ms ease, box-shadow 180ms ease, background-color 180ms ease, filter 180ms ease, opacity 140ms ease",

    WebkitTapHighlightColor: "transparent",
  },

  buttonLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  dropdown: {
    position: "fixed",
    zIndex: DROPDOWN_Z_INDEX,
    borderRadius: 0,
    overflow: "hidden",
  },

  dropdownHeader: {
    padding: "11px 13px 9px",
  },

  dropdownTitle: {
    display: "block",
    fontFamily: "'Open Sans', Inter, system-ui, sans-serif",
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1.25,
    color: "#0f172a",
  },

  dropdownSubtitle: {
    display: "block",
    marginTop: 3,
    fontFamily: "'Open Sans', Inter, system-ui, sans-serif",
    fontSize: 11.5,
    fontWeight: 400,
    lineHeight: 1.25,
  },

  optionsList: {
    maxHeight: 285,
    overflowY: "auto",
    padding: "7px 7px 6px",
  },

  option: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minHeight: 31,
    cursor: "pointer",
    padding: "5px 7px",
    borderRadius: 0,
    transition: "background-color 140ms ease",
  },

  checkbox: {
    width: 14,
    height: 14,
    flexShrink: 0,
    cursor: "pointer",
  },

  optionLabel: {
    flex: 1,
    minWidth: 0,
    fontFamily: "'Open Sans', Inter, system-ui, sans-serif",
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  optionRender: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },

  dropdownFooter: {
    padding: 9,
    background: "#ffffff",
  },

  confirmButton: {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    fontFamily: "'Open Sans', Inter, system-ui, sans-serif",
    fontWeight: 700,
    lineHeight: 1,

    border: "none",
    borderRadius: 0,
    outline: "none",
    cursor: "pointer",

    letterSpacing: "0.01em",

    transition:
      "transform 140ms ease, box-shadow 180ms ease, background-color 180ms ease, filter 180ms ease",
  },
};

export default TableActions;
